import React, { useState, useEffect } from 'react';
import { LogsTable } from '../components/LogsTable';
import { Pagination } from '../components/Pagination';
import { SkeletonTable, SkeletonCard } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { logsAPI } from '../lib/api';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS } from '../lib/theme';

export function LogsPage() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const logsPerPage = 10;

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterActive]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: logsPerPage,
      };

      if (filterActive && startDate) params.start_date = startDate;
      if (filterActive && endDate) params.end_date = endDate;

      const response = await logsAPI.getLogs(params);
      setLogs(response.data.logs);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      toast.error('Gagal memuat log');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
    setFilterActive(true);
  };

  const handleFilterClear = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setFilterActive(false);
  };

  const handleDeleteLogs = async () => {
    if (!startDate || !endDate) {
      toast.error('Pilih tanggal mulai dan akhir');
      return;
    }

    setDeleting(true);
    try {
      await logsAPI.deleteLogs(startDate, endDate);
      toast.success('Log berhasil dihapus');
      setDeleteDialogOpen(false);
      setCurrentPage(1);
      loadLogs();
    } catch (error) {
      toast.error('Gagal menghapus log');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="h-5 w-48 bg-muted animate-pulse rounded-sm" />
          <div className="h-3 w-64 bg-muted animate-pulse rounded-sm" />
        </div>
        <SkeletonCard />
        <SkeletonTable />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumb + Page Header */}
      <div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
          <span>Manajemen</span>
          <span>›</span>
          <span style={{ color: STATUS.primary.text }} className="font-medium">Log Aktivitas</span>
        </div>
        <h2 className="text-base sm:text-lg font-semibold tracking-tight">Log Aktivitas</h2>
        <p className="text-muted-foreground mt-0.5 text-xs">
          Riwayat aktivitas sistem monitoring
        </p>
      </div>

      {/* Filter Card */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="start-date">Tanggal Mulai</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="end-date">Tanggal Akhir</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleFilterApply}
                disabled={!startDate && !endDate}
                className="w-full sm:w-auto"
              >
                Saring
              </Button>
              <Button
                variant="outline"
                onClick={handleFilterClear}
                className="w-full sm:w-auto"
              >
                Hapus Filter
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={!startDate || !endDate}
                className="w-full sm:w-auto sm:ml-auto"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                Hapus Log
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <LogsTable logs={logs} />

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-md mx-4 sm:mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Log Aktivitas</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus semua log dari{' '}
              <strong>{startDate}</strong> sampai <strong>{endDate}</strong>?
              <br /><br />
              <span style={{ color: STATUS.critical.text }} className="font-semibold">
                ⚠️ Tindakan ini tidak dapat dibatalkan!
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLogs}
              disabled={deleting}
              className="w-full sm:w-auto text-white"
              style={{ background: STATUS.critical.icon }}
            >
              {deleting ? 'Menghapus...' : 'Ya, Hapus Log'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}